import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useReveal } from "@/hooks/useReveal";

const INTRO =
  "Engineers and operators who have built, commissioned and run biomass plants. We bring that hands-on experience to every project we take on.";

const TEAM_TOP = [
  {
    name: "Rohit Nagargoje",
    role: "Chief Executive Officer",
    photo: "rohit-nagargoje.png",
    linkedin: "https://www.linkedin.com/in/rohitnagargoje/",
    bio: "Has spent his career at the intersection of engineering and commercial strategy in the carbon removal and biofuel space, having co-founded a biochar and biofuel venture and helped scale it from early pilots to commercial operations.",
  },
  {
    name: "Bobby Choudhary",
    role: "Chief Operating Officer",
    photo: "bobby-choudhary.png",
    linkedin: "https://www.linkedin.com/in/bobbychoudhary/",
    bio: "Built his career on the plant floor — running test centers and commissioning pyrolysis and biochar units. He has spent years in the gap between a design on paper and a plant that actually runs continuously at 80%+ OEE.",
  },
  {
    name: "Srikanth Vishwanath",
    role: "Co-founder & Head of Carbon Removal",
    photo: "srikanth-vishwanath.png",
    linkedin: "https://www.linkedin.com/in/srikanthvishwanath/",
    bio: "Leads due diligence on carbon removal transactions, structuring offtakes, and driving certification under standards including Puro.earth, CSI, and ISCC EU, alongside designing MRV systems.",
  },
];

const TEAM_BELOW = [
  {
    name: "Krishna Hara Chakravarty, PhD",
    role: "Co-founder, Bio-Oil & Carbon R&D",
    photo: "krishna-hara-chakravarty.png",
    linkedin: "https://www.linkedin.com/in/krich2/",
    bio: "Brings more than 13 years of experience in biomass valorization pathways, having led research with first-principles thinking on the zero to one for biochar and bio-oil.",
  },
  {
    name: "Dhananjay Pachankar",
    role: "Senior Manager, Biochar Sales & Partnership",
    photo: "dhananjay-pachankar.png",
    linkedin: "https://www.linkedin.com/in/dhananjaypachankar/",
    bio: "Draws on 11+ years in the agriculture sector across sales, marketing and business development. Has field experience with biochar and biofertilizers, and has run biochar crop demonstrations across various areas and soil types.",
  },
];

const ADVISORY = [
  {
    name: "Mogens Slot Knudsen",
    photo: "mogens-slot-knudsen.png",
    linkedin: "https://www.linkedin.com/in/mogens-slot-knudsen-8b762b1/",
    bio: "Danish business leader with 40–50 years in international management, sales and M&A, and deep knowledge of biomass, biogas and the related machinery. Former MD and co-owner of Briket Holding and Kinetic Biofuel; board member of C.F. Nielsen A/S (RUF Group) since 2007.",
  },
  {
    name: "Prof. Thomas Howard",
    photo: "thomas-howard.png",
    linkedin: "https://www.linkedin.com/in/thomas-j-howard/",
    bio: "Professor of Technology Entrepreneurship at the Technical University of Denmark (DTU), where he is also Head of Innovation. Runs DTU X-Tech, the university’s flagship incubator for hard-tech spin-outs, and works on robust design, reliability engineering and start-up governance.",
  },
  {
    name: "Vinesh Sadekar",
    photo: "vinesh-sadekar.png",
    linkedin: "https://www.linkedin.com/in/vinesh-sadekar-43b76416/",
    bio: "Over 40+ years of leadership across petrochemicals, pharma and speciality chemicals, including CEO/MD roles at RPG Group companies, Cheminova, Navin Fluorine and Deepak Fertilisers. Founder MD of an energy-efficiency and air/water treatment company, and an experienced independent director on listed company boards.",
  },
  {
    name: "Vinod Nair",
    photo: "vinod-nair.png",
    linkedin: "https://www.linkedin.com/in/vinod-nair-b6511643/",
    bio: "A Chartered Accountant with 30+ years of experience in setting up companies, running finance and compliance functions. Provides finance, legal and statutory services to corporates.",
  },
];

const slug = (name) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const PersonCard = ({ person, variant, delay }) => {
  const NameTag = variant === "team" ? "h2" : "h3";
  const reveal = useReveal(delay);
  return (
    <div
      ref={reveal.ref}
      style={reveal.style}
      data-testid={`${variant}-card-${slug(person.name)}`}
      className="h-full flex flex-col items-center gap-5"
    >
      <img
        src={`/team-photos/${person.photo}`}
        alt=""
        width={176}
        height={176}
        className="w-[176px] h-[176px] rounded-full object-cover bg-[#E8EEF6] shrink-0"
      />
      <NameTag className="!text-[22px] !leading-[1.25] !font-semibold !tracking-[-0.03em] !text-[#1B4C91] text-center m-0">
        {person.name}
      </NameTag>
      {person.role && (
        <div className="-mt-[14px] text-[16px] leading-[1.5] text-[#4A4A4A] text-center">
          {person.role}
        </div>
      )}
      <p className="-mt-[8px] text-[16px] leading-[1.6] text-[#5B6675] text-justify m-0">
        {person.bio}
      </p>
      <a
        href={person.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${person.name} on LinkedIn`}
        data-testid={`${variant}-linkedin-${slug(person.name)}`}
        className="mt-auto self-start inline-flex items-center gap-1.5 min-h-[44px] text-[15px] font-semibold text-[#1B4C91] hover:underline"
      >
        LinkedIn
        <ArrowUpRight size={14} />
      </a>
    </div>
  );
};

const AdvisoryHeading = () => {
  const reveal = useReveal(0);
  return (
    <h2
      ref={reveal.ref}
      style={reveal.style}
      className="!text-[28px] !font-bold !leading-[1.25] !tracking-[-0.01em] !text-[#1B4C91] m-0"
    >
      Advisory board
    </h2>
  );
};

const Team = () => {
  usePageMeta("Our Team | Tanren", INTRO);
  return (
    <div data-testid="page-team">
      {/* INTRO — full-screen banner */}
      <section
        data-testid="team-intro"
        className="bg-[#F4F7FB] pt-[128px] pb-[56px] md:pt-[168px] md:pb-[80px]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h1 className="text-[40px] md:text-[64px] font-bold leading-[1.05] tracking-[-0.02em] text-[#1B4C91] m-0">
            Our team
          </h1>
          <p className="mt-6 max-w-[680px] text-[20px] leading-[1.6] text-[#4A4A4A]">
            {INTRO}
          </p>
        </div>
      </section>

      {/* TEAM */}
      <section data-testid="team-grid-section" className="bg-white py-[56px] md:pt-[88px] md:pb-[104px]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-y-16 gap-x-8 xl:gap-x-16">
            {TEAM_TOP.map((p, i) => (
              <PersonCard
                key={p.name}
                person={p}
                variant="team"
                delay={() => (window.innerWidth >= 1280 ? (i % 3) * 120 : 0)}
              />
            ))}
          </div>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-y-16 gap-x-8 xl:gap-x-16">
            {TEAM_BELOW.map((p, i) => (
              <PersonCard
                key={p.name}
                person={p}
                variant="team"
                delay={() => (window.innerWidth >= 1280 ? (i % 3) * 120 : 0)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ADVISORY BOARD */}
      <section data-testid="team-advisory-section" className="bg-[#F4F7FB] py-[56px] md:pt-[88px] md:pb-[96px]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <AdvisoryHeading />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-y-14 gap-x-8 xl:gap-x-16">
            {ADVISORY.map((p, i) => (
              <PersonCard
                key={p.name}
                person={p}
                variant="advisory"
                delay={() => (window.innerWidth >= 640 && i % 2 === 1 ? 120 : 0)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT BAND */}
      <section data-testid="team-contact-band" className="bg-[#1B4C91] py-[56px] md:py-[80px]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="!text-[28px] md:!text-[40px] !font-bold !leading-[1.15] !tracking-[-0.01em] !text-white m-0">
              Have a project in mind?
            </h2>
            <p className="mt-3 text-[18px] text-[#DCE6F5]">
              Tell us about your biomass and what you want to make from it. We will get back to you.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="team-contact-cta"
            className="inline-flex items-center justify-center h-[52px] px-8 rounded-full bg-[#D9A441] text-[#12305F] text-[16px] font-bold shrink-0 hover:underline"
          >
            Contact us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Team;
